import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Chip
} from '@mui/material';
import { subscriptionService } from '../services/subscriptionService';

const PromotionCodeInput = ({ onPromotionApplied, planType = 'plus', disabled = false }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validPromotion, setValidPromotion] = useState(null);

  const validatePromotion = async () => {
    if (!code.trim()) {
      setError('Please enter a promotion code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await subscriptionService.validatePromotionCode(code.trim(), planType);
      
      if (result.valid) {
        setValidPromotion(result);
        setSuccess(`✅ ${result.description || result.promotion?.name} - ${result.discount_display}!`);
        
        // Notify parent component
        if (onPromotionApplied) {
          onPromotionApplied({
            code: code.trim().toUpperCase(),
            promotion: result.promotion,
            discount_display: result.discount_display,
            valid: true
          });
        }
      } else {
        setError(result.message || 'Invalid promotion code');
        setValidPromotion(null);
        if (onPromotionApplied) {
          onPromotionApplied(null);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to validate promotion code');
      setValidPromotion(null);
      if (onPromotionApplied) {
        onPromotionApplied(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearPromotion = () => {
    setCode('');
    setError('');
    setSuccess('');
    setValidPromotion(null);
    if (onPromotionApplied) {
      onPromotionApplied(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && code.trim()) {
      validatePromotion();
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Promotion Code
      </Typography>
      
      {validPromotion ? (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`${validPromotion.promotion?.code} - ${validPromotion.discount_display}`}
            color="success"
            onDelete={clearPromotion}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="success.main">
            {validPromotion.description}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Enter promotion code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g., WELCOME50"
            disabled={loading || disabled}
            onKeyPress={handleKeyPress}
            sx={{ flex: 1 }}
          />
          <Button 
            variant="outlined"
            onClick={validatePromotion}
            disabled={!code.trim() || loading || disabled}
            sx={{ minWidth: 100 }}
          >
            {loading ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      
      {success && !validPromotion && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default PromotionCodeInput;
